import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import HistoryIcon from '@mui/icons-material/History';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { SmartFilter } from '../../components/common/SmartFilter';
import withComponentAuth from '../../hoc/withComponentAuth';
import { dashboardTokens } from '../Dashboard/Components/dashboardCardStyles';
import { useAdminPermission } from '../../hooks/useAdminPermission';
import useSnackbar from '../../hooks/useSnackbar';
import {
  PartnerLead,
  PartnerLeadStatus,
  getPartnerLead,
  getPartnerLeadPhotoUrl,
  updatePartnerLeadStatus,
} from '../../services/channelPartners';
import { LeadStatusChip, leadStatusMeta, pretty } from './PartnerLeads';
import { ConvertPartnerLeadPanel } from './ConvertPartnerLeadPanel';

const TRANSITIONS: Record<PartnerLeadStatus, PartnerLeadStatus[]> = {
  SUBMITTED: ['UNDER_REVIEW', 'REJECTED', 'CANCELLED', 'EXPIRED'],
  UNDER_REVIEW: ['CONTACTED', 'REJECTED', 'CANCELLED', 'EXPIRED'],
  CONTACTED: ['CONVERTED', 'REJECTED', 'CANCELLED'],
  CONVERTED: [],
  REJECTED: [],
  CANCELLED: [],
  EXPIRED: [],
};

const REASON_REQUIRED: PartnerLeadStatus[] = ['REJECTED', 'CANCELLED', 'EXPIRED'];

const initials = (name?: string) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

const Value = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: dashboardTokens.textMuted, textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography sx={{ color: '#e2e8f0', fontSize: 14, mt: 0.25, overflowWrap: 'anywhere' }}>{value || 'N/A'}</Typography>
  </Box>
);

const Section = ({
  title,
  icon,
  action,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Paper
    sx={{
      p: 2.5,
      height: '100%',
      bgcolor: dashboardTokens.cardBg,
      border: `1px solid ${dashboardTokens.border}`,
      borderRadius: '16px',
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
      <Box display="flex" alignItems="center" gap={1}>
        {icon && (
          <Box sx={{ display: 'flex', color: '#a78bfa' }}>{icon}</Box>
        )}
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{title}</Typography>
      </Box>
      {action}
    </Box>
    {children}
  </Paper>
);

const formatAddress = (lead: PartnerLead) =>
  [lead.address?.line1, lead.address?.line2, lead.address?.town, lead.address?.county, lead.address?.postcode]
    .filter(Boolean)
    .join(', ');

const PartnerLeadDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { canEdit } = useAdminPermission();
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [lead, setLead] = useState<PartnerLead | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [targetStatus, setTargetStatus] = useState<PartnerLeadStatus | null>(null);
  const [reason, setReason] = useState('');
  // CONVERTED doesn't go through the plain status-update Dialog below — the
  // server rejects it there (see partnerLead.service.js#updateStatus) because
  // converting has to create a real customer + Enquiry. It opens the
  // dedicated conversion panel instead.
  const [convertPanelOpen, setConvertPanelOpen] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);

  const fetchLead = useCallback(async () => {
    if (!leadId) return;
    try {
      setLoading(true);
      setLead(await getPartnerLead(leadId));
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to load partner lead', 'error');
    } finally {
      setLoading(false);
    }
  }, [leadId, showSnackbar]);

  useEffect(() => {
    fetchLead();
  }, [fetchLead]);

  const nextStatuses = useMemo(() => (lead ? TRANSITIONS[lead.status] : []), [lead]);
  const reasonRequired = targetStatus ? REASON_REQUIRED.includes(targetStatus) : false;
  const canUpdateStatus = canEdit('partnerLeads');

  const submitStatus = async () => {
    if (!lead || !targetStatus) return;
    if (reasonRequired && !reason.trim()) return;

    try {
      setSaving(true);
      await updatePartnerLeadStatus(lead._id, {
        status: targetStatus,
        statusReason: reasonRequired ? reason.trim() : undefined,
      });
      showSnackbar('Lead status updated', 'success');
      setTargetStatus(null);
      setReason('');
      await fetchLead();
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to update status', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openPhoto = async (index: number) => {
    if (!lead) return;
    try {
      const { signedUrl } = await getPartnerLeadPhotoUrl(lead._id, index);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to open photo', 'error');
    }
  };

  const openStatusMenu = (event: React.MouseEvent<HTMLElement>) => setStatusMenuAnchor(event.currentTarget);
  const closeStatusMenu = () => setStatusMenuAnchor(null);
  const chooseStatus = (status: PartnerLeadStatus) => {
    closeStatusMenu();
    if (status === 'CONVERTED') setConvertPanelOpen(true);
    else setTargetStatus(status);
  };

  const noActionsReason = !canUpdateStatus
    ? 'Status updates require edit permission'
    : nextStatuses.length === 0
    ? 'Terminal status — no further actions'
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {SnackbarComponent}
      <SmartFilter
        title="Partner Lead Detail"
        enableBack
        createFilter={[]}
        extraActions={lead ? <LeadStatusChip status={lead.status} /> : null}
      />

      <Box sx={{ p: { xs: 1.5, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2.5, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={26} sx={{ color: '#7B1FA2' }} />
          </Box>
        ) : !lead ? (
          <Paper sx={{ p: 3, bgcolor: dashboardTokens.cardBg, color: dashboardTokens.textSecondary, border: `1px solid ${dashboardTokens.border}`, borderRadius: '16px', textAlign: 'center' }}>
            Lead not found
          </Paper>
        ) : (
          <>
            <Paper
              sx={{
                p: 3,
                bgcolor: dashboardTokens.cardBg,
                border: `1px solid ${dashboardTokens.border}`,
                borderRadius: '16px',
                backgroundImage: 'linear-gradient(135deg, rgba(123,31,162,0.10), rgba(123,31,162,0) 60%)',
              }}
            >
              <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap" alignItems="flex-start">
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ width: 52, height: 52, bgcolor: '#7B1FA2', color: '#fff', fontWeight: 700, fontSize: 18 }}>
                    {initials(lead.customerName)}
                  </Avatar>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
                      <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 20, letterSpacing: '-0.01em' }}>
                        {lead.channelPartnerLeadId}
                      </Typography>
                      <LeadStatusChip status={lead.status} />
                    </Box>
                    <Typography sx={{ color: dashboardTokens.textSecondary, fontSize: 13.5, mt: 0.25 }}>
                      {lead.customerName} · {lead.customerPhoneNumber}
                    </Typography>
                    <Typography sx={{ color: dashboardTokens.textMuted, fontSize: 11.5, mt: 0.75 }}>
                      Created {dayjs(lead.createdAt).format('D MMM YYYY, h:mm A')} · Updated {dayjs(lead.updatedAt).format('D MMM YYYY, h:mm A')}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  {noActionsReason ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: dashboardTokens.textMuted, fontSize: 12.5, px: 1.5, py: 1, borderRadius: 2, border: `1px solid ${dashboardTokens.border}` }}>
                      <LockOutlinedIcon sx={{ fontSize: 15 }} /> {noActionsReason}
                    </Box>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        onClick={openStatusMenu}
                        endIcon={<ArrowDropDownIcon />}
                        sx={{
                          bgcolor: '#7B1FA2',
                          '&:hover': { bgcolor: '#5E167A' },
                          textTransform: 'none',
                          fontWeight: 700,
                          borderRadius: '10px',
                          px: 2.25,
                        }}
                      >
                        Update status
                      </Button>
                      <Menu
                        anchorEl={statusMenuAnchor}
                        open={Boolean(statusMenuAnchor)}
                        onClose={closeStatusMenu}
                        PaperProps={{ sx: { bgcolor: '#1c1c1c', color: '#fff', border: `1px solid ${dashboardTokens.border}`, borderRadius: 2, mt: 0.5, minWidth: 200 } }}
                      >
                        {nextStatuses.map((status) => (
                          <MenuItem key={status} onClick={() => chooseStatus(status)} sx={{ fontSize: 13.5, gap: 1 }}>
                            <ListItemIcon sx={{ minWidth: 'auto !important' }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: leadStatusMeta[status].color }} />
                            </ListItemIcon>
                            <ListItemText>{leadStatusMeta[status].label}</ListItemText>
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                </Box>
              </Box>
            </Paper>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Section title="Customer" icon={<PersonOutlineIcon fontSize="small" />}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}><Value label="Name" value={lead.customerName} /></Grid>
                      <Grid item xs={12} sm={6}><Value label="Phone" value={lead.customerPhoneNumber} /></Grid>
                      <Grid item xs={12}><Value label="Address" value={formatAddress(lead)} /></Grid>
                    </Grid>
                  </Section>

                  <Section title="Product" icon={<HandymanOutlinedIcon fontSize="small" />}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}><Value label="Product type" value={pretty(lead.productType)} /></Grid>
                      <Grid item xs={6} sm={3}><Value label="Job type" value={pretty(lead.jobType)} /></Grid>
                      <Grid item xs={6} sm={3}><Value label="Equipment" value={pretty(lead.equipmentType)} /></Grid>
                      <Grid item xs={6} sm={3}><Value label="Quantity" value={lead.quantity} /></Grid>
                    </Grid>
                  </Section>

                  <Section title="Notes" icon={<DescriptionOutlinedIcon fontSize="small" />}>
                    <Typography sx={{ color: '#e2e8f0', fontSize: 14, whiteSpace: 'pre-wrap' }}>
                      {lead.notes || 'No notes added'}
                    </Typography>
                    {lead.statusReason && (
                      <Box mt={2} pt={2} sx={{ borderTop: `1px solid ${dashboardTokens.border}` }}>
                        <Value label="Status reason" value={lead.statusReason} />
                      </Box>
                    )}
                  </Section>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Section title="Partner">
                    {lead.channelPartner?._id ? (
                      <Box
                        onClick={() => navigate(`/channel-partners/${lead.channelPartner?._id}`)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          p: 1.25,
                          borderRadius: 2,
                          border: `1px solid ${dashboardTokens.border}`,
                          cursor: 'pointer',
                          '&:hover': { borderColor: '#444', bgcolor: '#1f1f1f' },
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#7B1FA2', color: '#fff', fontSize: 12.5, fontWeight: 700 }}>
                            {initials(lead.channelPartner.fullName || lead.channelPartner.phoneNumber || '?')}
                          </Avatar>
                          <Box minWidth={0}>
                            <Typography sx={{ color: '#fff', fontSize: 13.5, fontWeight: 600, overflowWrap: 'anywhere' }}>
                              {lead.channelPartner.fullName || 'Unnamed partner'}
                            </Typography>
                            <Typography sx={{ color: dashboardTokens.textSecondary, fontSize: 12 }}>
                              {lead.channelPartner.phoneNumber}
                            </Typography>
                          </Box>
                        </Box>
                        <OpenInNewIcon sx={{ fontSize: 16, color: dashboardTokens.textMuted, flexShrink: 0 }} />
                      </Box>
                    ) : (
                      <Typography sx={{ color: dashboardTokens.textMuted, fontSize: 13.5 }}>N/A</Typography>
                    )}
                  </Section>

                  <Section title="Photos" icon={<PhotoLibraryOutlinedIcon fontSize="small" />}>
                    {lead.photos?.length ? (
                      <Grid container spacing={1.25}>
                        {lead.photos.map((photo, index) => (
                          <Grid item xs={4} key={`${photo.storageKey}-${index}`}>
                            <Box
                              onClick={() => openPhoto(index)}
                              sx={{
                                aspectRatio: '1',
                                borderRadius: 2,
                                border: `1px solid ${dashboardTokens.border}`,
                                bgcolor: '#1f1f1f',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                '&:hover': { borderColor: '#7B1FA2', bgcolor: '#221a26' },
                              }}
                            >
                              {photo.contentType === 'application/pdf' ? (
                                <InsertDriveFileOutlinedIcon sx={{ color: '#a78bfa', fontSize: 22 }} />
                              ) : (
                                <ImageIcon sx={{ color: '#a78bfa', fontSize: 22 }} />
                              )}
                              <Typography sx={{ color: dashboardTokens.textMuted, fontSize: 10.5 }}>#{index + 1}</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    ) : <Typography sx={{ color: dashboardTokens.textMuted, fontSize: 13.5 }}>No photos attached</Typography>}
                  </Section>
                </Box>
              </Grid>
            </Grid>

            <Section title="Timeline" icon={<HistoryIcon fontSize="small" />}>
              {lead.events?.length ? (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {lead.events.map((event, index) => (
                    <Box key={event._id} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 10 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: leadStatusMeta[event.toStatus].color, flexShrink: 0, mt: 0.4 }} />
                        {index < (lead.events?.length || 0) - 1 && (
                          <Box sx={{ width: '1px', flex: 1, bgcolor: dashboardTokens.border, my: 0.5 }} />
                        )}
                      </Box>
                      <Box sx={{ pb: index < (lead.events?.length || 0) - 1 ? 2.25 : 0, flex: 1, minWidth: 0 }}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          {event.fromStatus && (
                            <Typography sx={{ fontSize: 12.5, color: dashboardTokens.textMuted }}>{pretty(event.fromStatus)} →</Typography>
                          )}
                          <Chip label={pretty(event.toStatus)} size="small" sx={{ bgcolor: leadStatusMeta[event.toStatus].bg, color: leadStatusMeta[event.toStatus].color, fontWeight: 700, height: 20 }} />
                          <Typography sx={{ fontSize: 11.5, color: dashboardTokens.textMuted }}>by {pretty(event.actor)}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 11.5, color: dashboardTokens.textMuted, mt: 0.25 }}>
                          {dayjs(event.createdAt).format('D MMM YYYY, h:mm A')}
                        </Typography>
                        {event.reason && (
                          <Typography sx={{ fontSize: 13, color: '#e2e8f0', mt: 0.5 }}>{event.reason}</Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ color: dashboardTokens.textMuted, fontSize: 13.5 }}>No events yet</Typography>
              )}
            </Section>
          </>
        )}
      </Box>

      <Dialog open={Boolean(targetStatus)} onClose={() => !saving && setTargetStatus(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { bgcolor: dashboardTokens.cardBg, color: '#fff', border: `1px solid ${dashboardTokens.border}`, borderRadius: 3, backgroundImage: 'none' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700 }}>
          Move to {targetStatus ? leadStatusMeta[targetStatus].label : ''}?
          <IconButton onClick={() => !saving && setTargetStatus(null)} sx={{ color: '#94a3b8' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: dashboardTokens.border }} />
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography sx={{ color: dashboardTokens.textSecondary, fontSize: 13, mb: 2 }}>
            This updates the partner lead lifecycle and writes an audit event.
          </Typography>
          {reasonRequired && (
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={3}
              label="Reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              InputLabelProps={{ sx: { color: '#94a3b8' } }}
              sx={{ textarea: { color: '#fff' }, '.MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}
            />
          )}
        </DialogContent>
        <Divider sx={{ borderColor: dashboardTokens.border }} />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTargetStatus(null)} disabled={saving} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitStatus}
            disabled={saving || (reasonRequired && !reason.trim())}
            sx={{ bgcolor: '#7B1FA2', '&:hover': { bgcolor: '#5E167A' }, textTransform: 'none', borderRadius: '10px' }}
          >
            {saving ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConvertPartnerLeadPanel
        lead={convertPanelOpen ? lead : null}
        onClose={() => setConvertPanelOpen(false)}
        onConverted={() => {
          fetchLead();
        }}
      />
    </Box>
  );
};

export default withComponentAuth(PartnerLeadDetail);